import { Download } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import {
  getPublications,
  unpublishInvoiceDebt,
} from 'lib/api/documentPublication';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import BreadCrumb from 'lib/designSystem/BreadCrumb';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PublicationForm from './components/PublicationForm';

type PublicationRow = {
  type: 'Factura' | 'Contrato Impago';
  creditorIdentifier: string;
  creditorName: string;
  folio: string;
  amount: number;
  createdAt: string;
  unpublishedAt: string;
};

const headers = [
  { key: 'type', label: 'Tipo de publicación' },
  { key: 'creditorIdentifier', label: 'RUT moroso' },
  { key: 'creditorName', label: 'Razón social' },
  { key: 'folio', label: 'Folio' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'createdAt', label: 'Fecha de publicación', type: 'date' },
  { key: 'unpublishedAt', label: 'Fecha de despublicación', type: 'date' },
];

const InvoicePublication: React.FC = () => {
  const [openNewInvoice, setOpenNewInvoice] = React.useState(false);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [publications, setPublications] = useState<PublicationRow[]>([]);
  const [unpublishSuccessDialogOpen, setUnpublishSuccessDialogOpen] =
    useState(false);
  const [unpublishCertificateLink, setUnpublishCertificateLink] =
    useState<string>('');
  const [showUnpublished, setShowUnpublished] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleUnpublishSuccessDialogClose = () => {
    setUnpublishSuccessDialogOpen(false);
    setUnpublishCertificateLink('');
  };

  const handleDownloadUnpublishCertificate = () => {
    if (unpublishCertificateLink) {
      window.open(unpublishCertificateLink, '_blank');
    }
  };

  const validateIntegrations = async () => {
    try {
      const response = await getIntegrationStatusByInternalName(
        dispatch,
        'cobranza-online',
      );
      return response;
    } catch (error) {
      navigate('/integrations');
    }
  };

  const fetchPublications = async () => {
    setLoading(true);
    const validated = await validateIntegrations();
    if (!validated.status) {
      navigate('/integrations');
    }
    const response: PublicationRow[] = await getPublications(dispatch);

    const filteredPublications = showUnpublished
      ? response
      : response.filter((pub) => pub.unpublishedAt === null);

    setPublications(filteredPublications || []);
    setLoading(false);
  };

  const handleUnpublish = async (id: number) => {
    setLoading(true);
    try {
      const response = await unpublishInvoiceDebt(dispatch, id);
      const certificateLink = response.certificateLink;
      setReload(reload + 1);
      setUnpublishCertificateLink(certificateLink);
      setUnpublishSuccessDialogOpen(true);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Hubo un problema al despublicar la factura, intente de nuevo',
      );
    }
    setLoading(false);
  };

  const actions = [
    {
      action: handleUnpublish,
      actionLabel: 'Despublicar',
    },
  ];

  useEffect(() => {
    void fetchPublications();
  }, [reload, showUnpublished]);

  return (
    <div>
      <BreadCrumb
        links={[
          {
            title: 'Integraciones',
            link: '/integrations',
          },
          {
            title: 'Cobranza Online',
            link: '/integrations/cobranza-online',
          },
        ]}
      />
      <HeaderWithActions
        title="Documentos publicados en servicios"
        variant={'h6'}
        buttons={[
          {
            name: 'Publicar',
            action: () => {
              setOpenNewInvoice(true);
            },
            color: 'primary',
          },
        ]}
        item={
          <FormControlLabel
            control={
              <Switch
                checked={showUnpublished}
                onChange={(e) => setShowUnpublished(e.target.checked)}
                color="primary"
              />
            }
            label="Mostrar documentos despublicados"
          />
        }
      />
      {loading && <CenteredCircularProgress />}
      {!loading && (
        <EnhancedTable
          dataHeaders={headers}
          data={publications}
          showActions
          rowsPerPageDefault={25}
          actions={actions}
        />
      )}
      <PublicationForm open={openNewInvoice} setOpen={setOpenNewInvoice} />
      <Dialog
        open={unpublishSuccessDialogOpen}
        onClose={handleUnpublishSuccessDialogClose}
        aria-labelledby="unpublish-success-dialog-title"
        aria-describedby="unpublish-success-dialog-description"
      >
        <DialogTitle id="unpublish-success-dialog-title">
          Documento Despublicado
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            El documento ha sido despublicado exitosamente.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Puedes descargar el certificado de despublicación haciendo clic en
            el botón de descarga.
          </Typography>
          <Grid
            container
            justifyContent="flex-end"
            spacing={1}
            marginTop="10px"
          >
            <Grid item>
              <CustomButton
                color="primary"
                startIcon={<Download />}
                onClick={handleDownloadUnpublishCertificate}
              >
                Descargar Certificado
              </CustomButton>
            </Grid>
            <Grid item>
              <CustomButton onClick={handleUnpublishSuccessDialogClose}>
                Aceptar
              </CustomButton>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicePublication;
